import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Modal, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from 'react-native';
import { Audio } from 'expo-av';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassFill } from '../../v10/ui';

// ---------------------------------------------------------------------------
// NEMOS Recall — native replica of src/app/nemos/recall/page.tsx. Same
// `recalls` table (title/days/time/sound/cycles/enabled) and same `sounds`
// table for custom-uploaded sounds.
//
// Preset sounds: the web app serves these from its public/sounds directory
// via GET /api/sounds (id: `preset-<file>`, src: `/sounds/<file>`). Mobile
// fetches that same endpoint at `${EXPO_PUBLIC_API_BASE_URL}/api/sounds` and
// plays presets by streaming `${EXPO_PUBLIC_API_BASE_URL}/sounds/<file>`
// through expo-av — no bundled assets needed. Saved rows use the identical
// `sound_id` (`preset-<file>`) and `sound_src` (absolute URL) shape web
// writes, so a recall created on either platform resolves correctly on the
// other.
//
// Web-only remainder:
//  - Actual scheduled local notifications firing at the configured day/time
//    (the reason Recall exists) are NOT wired up in this pass — this ports
//    the CRUD/list/preview surface only, matching the task's request to
//    port the NEMOS tool 1:1 as a UI/data surface. (expo-notifications is
//    already a mobile dependency, so a follow-up could schedule these, but
//    that's a distinct feature from the web page being replicated here,
///    which also does not itself schedule OS-level notifications — it
//    persists rows to `recalls` for the desktop client's own alert timer.)
// ---------------------------------------------------------------------------

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL;

interface PresetSound {
  id: string;
  name: string;
  src: string;
}

interface CustomSound {
  id: string;
  name: string;
  dataUrl: string;
}

interface Recall {
  id: string;
  title: string;
  days: number[];
  specificDate: string | null;
  time: string;
  soundId: string | null;
  soundSrc: string | null;
  cycles: number;
  enabled: boolean;
}

const DAY_LABELS: { dow: number; label: string }[] = [
  { dow: 1, label: 'M' },
  { dow: 2, label: 'T' },
  { dow: 3, label: 'W' },
  { dow: 4, label: 'R' },
  { dow: 5, label: 'F' },
  { dow: 6, label: 'S' },
  { dow: 0, label: 'U' },
];
const DAY_FULL_NAMES: Record<number, string> = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };

function mapRow(row: any): Recall {
  return {
    id: row.id,
    title: row.title,
    days: row.days || [],
    specificDate: row.specific_date,
    time: row.alert_time,
    soundId: row.sound_id,
    soundSrc: row.sound_src,
    cycles: row.cycles,
    enabled: row.enabled,
  };
}

export function NemosRecall({ userId }: { userId: string }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  const [recalls, setRecalls] = useState<Recall[]>([]);
  const [presetSounds, setPresetSounds] = useState<PresetSound[]>([]);
  const [customSounds, setCustomSounds] = useState<CustomSound[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('08:00');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [specificDate, setSpecificDate] = useState('');
  const [cycles, setCycles] = useState(1);
  const [selectedSoundId, setSelectedSoundId] = useState('');
  const [selectedSoundSrc, setSelectedSoundSrc] = useState<string | null>(null);
  const [selectedSoundName, setSelectedSoundName] = useState('');

  const [itemToDelete, setItemToDelete] = useState<{ id: string; title: string } | null>(null);

  const soundRef = useRef<Audio.Sound | null>(null);

  const fetchRecalls = useCallback(async () => {
    const { data, error } = await supabase.from('recalls').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (!error) setRecalls((data || []).map(mapRow));
  }, [userId]);

  const fetchSounds = useCallback(async () => {
    const { data } = await supabase.from('sounds').select('*').eq('user_id', userId);
    if (data) setCustomSounds(data.map((r: any) => ({ id: r.id, name: r.name, dataUrl: r.data_url })));
  }, [userId]);

  const fetchPresetSounds = useCallback(async () => {
    if (!API_BASE_URL) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/sounds`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const sounds: PresetSound[] = await res.json();
      setPresetSounds(
        sounds.map((s) => ({ ...s, src: s.src.startsWith('http') ? s.src : `${API_BASE_URL}${s.src}` }))
      );
    } catch {
      // Preset list is best-effort — custom sounds still work if this fails.
    }
  }, []);

  useEffect(() => {
    fetchRecalls();
    fetchSounds();
    fetchPresetSounds();

    const recallsChannel = supabase
      .channel('nemos_recalls_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'recalls', filter: `user_id=eq.${userId}` }, () => fetchRecalls())
      .subscribe();
    const soundsChannel = supabase
      .channel('nemos_recall_sounds_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sounds', filter: `user_id=eq.${userId}` }, () => fetchSounds())
      .subscribe();

    return () => {
      supabase.removeChannel(recallsChannel);
      supabase.removeChannel(soundsChannel);
      if (soundRef.current) soundRef.current.unloadAsync().catch(() => {});
    };
  }, [userId, fetchRecalls, fetchSounds, fetchPresetSounds]);

  const handleSoundChange = (id: string, presets?: PresetSound[], customs?: CustomSound[]) => {
    const all = [
      ...(presets || presetSounds),
      ...(customs || customSounds).map((cs) => ({ id: cs.id, name: cs.name, src: cs.dataUrl })),
    ];
    const s = all.find((x) => x.id === id);
    if (s) {
      setSelectedSoundId(s.id);
      setSelectedSoundSrc(s.src);
      setSelectedSoundName(s.name);
    }
  };

  const testSound = async () => {
    if (!selectedSoundSrc) return;
    try {
      if (soundRef.current) {
        await soundRef.current.unloadAsync();
        soundRef.current = null;
      }
      await Audio.setAudioModeAsync({ playsInSilentModeIOS: true, staysActiveInBackground: false });
      const { sound } = await Audio.Sound.createAsync({ uri: selectedSoundSrc }, { shouldPlay: true, volume: 0.75 });
      soundRef.current = sound;
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) sound.unloadAsync().catch(() => {});
      });
    } catch (err: any) {
      Alert.alert('Playback error', err?.message || 'Could not play sound. You can still save this recall and try again later.');
    }
  };

  const toggleDay = (dow: number) => setSelectedDays((prev) => (prev.includes(dow) ? prev.filter((d) => d !== dow) : [...prev, dow]));

  const resetForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setTitle('');
    setTime('08:00');
    setSelectedDays([]);
    setSpecificDate('');
    setCycles(1);
    setSelectedSoundId('');
    setSelectedSoundSrc(null);
    setSelectedSoundName('');
  };

  const handleEdit = (recall: Recall) => {
    setIsEditing(true);
    setEditingId(recall.id);
    setTitle(recall.title);
    setTime(recall.time);
    setSelectedDays(recall.days);
    setSpecificDate(recall.specificDate || '');
    setCycles(recall.cycles);
    if (recall.soundId) handleSoundChange(recall.soundId);
    else {
      setSelectedSoundId('');
      setSelectedSoundSrc(null);
      setSelectedSoundName('');
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      Alert.alert('Title required');
      return;
    }
    const isPreset = presetSounds.some((p) => p.id === selectedSoundId);
    const recurrence = selectedDays.length === 7 ? 'daily' : selectedDays.length > 0 ? 'weekly' : 'none';
    const record: Record<string, any> = {
      user_id: userId,
      title: title.trim(),
      alert_time: time,
      sound_id: selectedSoundId || null,
      sound_src: isPreset ? selectedSoundSrc : null,
      cycles,
      days: selectedDays,
      recurrence,
      day_of_week: null,
      specific_date: selectedDays.length === 0 && specificDate ? specificDate : null,
      enabled: true,
      updated_at: new Date().toISOString(),
    };

    try {
      if (editingId) {
        const { error } = await supabase.from('recalls').update(record).eq('id', editingId);
        if (error) throw error;
      } else {
        record.created_at = new Date().toISOString();
        const { error } = await supabase.from('recalls').insert(record);
        if (error) throw error;
      }
      resetForm();
    } catch (err: any) {
      Alert.alert('Error saving recall', err?.message || 'Unknown error');
    }
  };

  const handleToggleEnabled = async (recall: Recall) => {
    const { error } = await supabase.from('recalls').update({ enabled: !recall.enabled, updated_at: new Date().toISOString() }).eq('id', recall.id);
    if (error) Alert.alert('Error', error.message);
  };

  const handleDeleteRecall = async () => {
    if (!itemToDelete) return;
    const { error } = await supabase.from('recalls').delete().eq('id', itemToDelete.id);
    if (error) Alert.alert('Error', error.message);
    setItemToDelete(null);
  };

  const describeSchedule = (r: Recall) => {
    if (r.days.length === 7) return `Every day at ${r.time}`;
    if (r.days.length > 0) {
      const labels = DAY_LABELS.filter((d) => r.days.includes(d.dow)).map((d) => d.label);
      return `${labels.join(' ')} at ${r.time}`;
    }
    if (r.specificDate) return `${r.specificDate} at ${r.time}`;
    return `Once at ${r.time}`;
  };

  const getSoundName = (r: Recall) => {
    if (!r.soundId) return 'No sound';
    const preset = presetSounds.find((x) => x.id === r.soundId);
    if (preset) return preset.name;
    const custom = customSounds.find((x) => x.id === r.soundId);
    if (custom) return custom.name;
    return 'Unknown sound';
  };

  const repeatSummary = useMemo(() => {
    if (selectedDays.length === 0) return 'No days selected — this will be a one-time recall.';
    if (selectedDays.length === 7) return 'Every day';
    return DAY_LABELS.filter((d) => selectedDays.includes(d.dow)).map((d) => DAY_FULL_NAMES[d.dow]).join(', ');
  }, [selectedDays]);

  return (
    <View style={styles.root}>
      {!isEditing ? (
        <>
          <View style={styles.headerRow}>
            <Text style={styles.sectionTitle}>Recalls</Text>
            <Pressable
              style={styles.iconBtn}
              onPress={() => {
                resetForm();
                setIsEditing(true);
              }}
            >
              <Text style={styles.iconBtnText}>+ New</Text>
            </Pressable>
          </View>
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {recalls.length === 0 && <Text style={styles.emptyText}>No recalls yet. Create one to get started.</Text>}
            {recalls.map((recall) => (
              <Pressable key={recall.id} style={styles.recallRow} onPress={() => handleEdit(recall)}>
                <Switch value={recall.enabled} onValueChange={() => handleToggleEnabled(recall)} />
                <View style={styles.recallInfo}>
                  <Text style={[styles.recallTitle, !recall.enabled && styles.recallTitleDisabled]} numberOfLines={1}>{recall.title}</Text>
                  <Text style={styles.recallMeta}>{describeSchedule(recall)}</Text>
                  <Text style={styles.recallMeta}>{getSoundName(recall)} · {recall.cycles}x</Text>
                </View>
                <Pressable style={styles.deleteBtn} onPress={() => setItemToDelete({ id: recall.id, title: recall.title })}>
                  <Text style={styles.deleteBtnText}>✕</Text>
                </Pressable>
              </Pressable>
            ))}
          </ScrollView>
        </>
      ) : (
        <ScrollView style={styles.scroll} contentContainerStyle={styles.formContent}>
          <Text style={styles.sectionTitle}>{editingId ? 'Edit Recall' : 'New Recall'}</Text>

          <View style={styles.field}>
            <Text style={styles.label}>Title</Text>
            <TextInput style={styles.input} value={title} onChangeText={setTitle} placeholder="e.g. Stand-up meeting" placeholderTextColor={t.ivorySoft} />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Time (HH:MM, 24h)</Text>
            <TextInput style={styles.input} value={time} onChangeText={setTime} placeholder="08:00" placeholderTextColor={t.ivorySoft} />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Repeat</Text>
            <View style={styles.dayRow}>
              {DAY_LABELS.map(({ dow, label }) => {
                const checked = selectedDays.includes(dow);
                return (
                  <Pressable key={dow} style={[styles.dayCircle, checked && styles.dayCircleActive]} onPress={() => toggleDay(dow)}>
                    <Text style={[styles.dayCircleText, checked && styles.dayCircleTextActive]}>{label}</Text>
                  </Pressable>
                );
              })}
            </View>
            <Text style={styles.hint}>{repeatSummary}</Text>
          </View>

          {selectedDays.length === 0 && (
            <View style={styles.field}>
              <Text style={styles.label}>Date (optional, YYYY-MM-DD)</Text>
              <TextInput style={styles.input} value={specificDate} onChangeText={setSpecificDate} placeholder="2026-07-14" placeholderTextColor={t.ivorySoft} />
            </View>
          )}

          <View style={styles.field}>
            <Text style={styles.label}>Sound</Text>
            {presetSounds.length > 0 && (
              <>
                <Text style={styles.subLabel}>Preset sounds</Text>
                <View style={styles.soundList}>
                  {presetSounds.map((s) => (
                    <Pressable key={s.id} style={[styles.soundChip, selectedSoundId === s.id && styles.soundChipActive]} onPress={() => handleSoundChange(s.id)}>
                      <Text style={[styles.soundChipText, selectedSoundId === s.id && styles.soundChipTextActive]} numberOfLines={1}>{s.name}</Text>
                    </Pressable>
                  ))}
                </View>
              </>
            )}
            <Text style={styles.subLabel}>Custom sounds</Text>
            {customSounds.length === 0 ? (
              <Text style={styles.hint}>No custom sounds yet. Upload one from the web app.</Text>
            ) : (
              <View style={styles.soundList}>
                {customSounds.map((s) => (
                  <Pressable key={s.id} style={[styles.soundChip, selectedSoundId === s.id && styles.soundChipActive]} onPress={() => handleSoundChange(s.id)}>
                    <Text style={[styles.soundChipText, selectedSoundId === s.id && styles.soundChipTextActive]} numberOfLines={1}>{s.name}</Text>
                  </Pressable>
                ))}
              </View>
            )}
            <Pressable style={styles.testBtn} onPress={testSound} disabled={!selectedSoundSrc}>
              <Text style={styles.testBtnText}>▶ Test sound</Text>
            </Pressable>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Cycles (sound repeats)</Text>
            <TextInput
              style={styles.input}
              keyboardType="number-pad"
              value={String(cycles)}
              onChangeText={(v) => setCycles(Math.max(1, parseInt(v, 10) || 1))}
            />
            <Text style={styles.hint}>How many times the sound plays before stopping.</Text>
          </View>

          <View style={styles.formButtons}>
            <Pressable style={styles.saveBtn} onPress={handleSave}>
              <Text style={styles.saveBtnText}>{editingId ? 'Update Recall' : 'Create Recall'}</Text>
            </Pressable>
            <Pressable style={styles.cancelBtn} onPress={resetForm}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </Pressable>
          </View>
        </ScrollView>
      )}

      <Modal visible={!!itemToDelete} transparent animationType="fade" onRequestClose={() => setItemToDelete(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Delete recall?</Text>
            <Text style={styles.modalBody}>This will permanently delete &quot;{itemToDelete?.title}&quot;.</Text>
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setItemToDelete(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnDanger]} onPress={handleDeleteRecall}>
                <Text style={[styles.modalBtnText, styles.modalBtnDangerText]}>Delete</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu, fonts, radii }) =>
  StyleSheet.create({
  root: { flex: 1 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  sectionTitle: { flex: 1, fontSize: 18, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  iconBtn: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  iconBtnText: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 24, gap: 6 },
  emptyText: { fontSize: 13, color: t.ivorySoft, textAlign: 'center', marginTop: 24 },

  recallRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 8, borderRadius: radii.tile, backgroundColor: t.glass, marginBottom: 6, borderWidth: 1, borderColor: t.navyLine },
  recallInfo: { flex: 1, gap: 1 },
  recallTitle: { fontSize: 14, fontWeight: '800', color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase' },
  recallTitleDisabled: { color: t.ivorySoft, textDecorationLine: 'line-through', fontFamily: fonts.title, textTransform: 'uppercase' },
  recallMeta: { fontSize: 11, color: t.ivorySoft },
  deleteBtn: { paddingHorizontal: 8, paddingVertical: 4 },
  deleteBtnText: { color: c.danger, fontSize: 14, fontWeight: '800' },

  formContent: { paddingBottom: 40, gap: 16 },
  field: { gap: 6 },
  label: { fontFamily: fonts.title, fontSize: 12, fontWeight: '800', color: t.ivorySoft, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { backgroundColor: c.inputFill, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 9, fontSize: 14, color: t.ivory, borderWidth: 1, borderColor: t.navyLine },
  hint: { fontSize: 11, color: t.ivorySoft, fontStyle: 'italic' },
  subLabel: { fontSize: 11, fontWeight: '800', color: t.ivorySoft, marginTop: 4 },
  dayRow: { flexDirection: 'row', gap: 8 },
  dayCircle: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  dayCircleActive: { backgroundColor: t.gold, ...neu.glowPrimary },
  dayCircleText: { fontSize: 13, fontWeight: '800', color: t.ivorySoft },
  dayCircleTextActive: { color: t.onGold },
  soundList: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  soundChip: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: radii.pill, backgroundColor: t.glass, maxWidth: 180, borderWidth: 1, borderColor: t.navyLine },
  soundChipActive: { backgroundColor: t.gold, ...neu.glowPrimary },
  soundChipText: { fontSize: 12, color: t.ivory },
  soundChipTextActive: { color: t.onGold, fontWeight: '800' },
  testBtn: { alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 7, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  testBtnText: { fontSize: 12, fontWeight: '800', color: t.goldBright },

  formButtons: { flexDirection: 'row', gap: 10, marginTop: 4 },
  saveBtn: { flex: 1, alignItems: 'center', paddingVertical: 12, borderRadius: radii.pill, backgroundColor: t.gold, ...neu.glowPrimary },
  saveBtnText: { fontWeight: '900', color: t.onGold },
  cancelBtn: { paddingHorizontal: 18, alignItems: 'center', justifyContent: 'center', borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  cancelBtnText: { fontWeight: '800', color: t.ivory },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modalCard: { backgroundColor: 'transparent', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, borderRadius: radii.panel, padding: 18, gap: 12, width: '100%', maxWidth: 380, boxShadow: `0 10px 30px ${t.glowSoft}` as any },
  modalTitle: { fontSize: 16, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  modalBody: { fontSize: 13, color: t.ivorySoft, lineHeight: 18 },
  modalButtons: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end' },
  modalBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.tile, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  modalBtnText: { fontWeight: '800', color: t.ivory },
  modalBtnDanger: { backgroundColor: c.danger },
  modalBtnDangerText: { color: c.white },
}),
);
